import { useCallback, useEffect, useRef, useState } from "react";

export const SIDEBAR_MIN_WIDTH = 230;
export const SIDEBAR_MAX_WIDTH = 500;

const SIDEBAR_DEFAULT_WIDTHS: Record<string, number> = {
  small: 300,
  medium: 330,
  large: 400,
  xLarge: 450,
};

const STORAGE_KEY = "sidebar-width";

function getScreenSize(): string {
  const width = window.innerWidth;
  if (width > 1800) return "xLarge";
  if (width > 1450) return "large";
  if (width > 720) return "medium";
  return "small";
}

function getDefaultWidth(): number {
  return SIDEBAR_DEFAULT_WIDTHS[getScreenSize()];
}

function getStoredWidth(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === null) return getDefaultWidth();

  const parsed = Number(stored);
  if (Number.isNaN(parsed)) return getDefaultWidth();

  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
}

export function useResizableSidebar() {
  const [sidebarWidth, setSidebarWidth] = useState(getStoredWidth);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const sidebarWidthRef = useRef(sidebarWidth);
  const hasUserResized = useRef(localStorage.getItem(STORAGE_KEY) !== null);

  useEffect(() => {
    sidebarWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);

  // If the user resizes the window, set the sidebar width to the new default width for that screen
  // regardless of whether they had previously resized the sidebar or not
  useEffect(() => {
    function handleWindowResize() {
      hasUserResized.current = false;
      localStorage.removeItem(STORAGE_KEY);
      setSidebarWidth(getDefaultWidth());
    }

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = true;
      startX.current = e.clientX;
      startWidth.current = sidebarWidth;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    [sidebarWidth],
  );

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isResizing.current) return;

      const resizeDifference = e.clientX - startX.current;
      const newWidth = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, startWidth.current + resizeDifference),
      );
      setSidebarWidth(newWidth);
    }

    function handleMouseUp() {
      if (!isResizing.current) return;

      isResizing.current = false;
      hasUserResized.current = true;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      localStorage.setItem(STORAGE_KEY, String(sidebarWidthRef.current));
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // Allow user to quickly resize the sidebar
  function handleDoubleClick() {
    const newWidth = hasUserResized.current
      ? getDefaultWidth()
      : SIDEBAR_MAX_WIDTH;
    setSidebarWidth(newWidth);
    localStorage.removeItem(STORAGE_KEY);
    hasUserResized.current = !hasUserResized.current;
  }

  return { sidebarWidth, handleMouseDown, handleDoubleClick };
}
